import { useEffect, useRef } from "react"
import { useSyncData } from "@hooks"
import { useSignageStore } from "@stores"
import { queryClient } from "@hocs"
import { ROUTE, SIGNAGE_PAGE_IDLE_TIMEOUT_MINUTE, SOCKET_EVENTS } from "@constants"
import { SIGNAGE_ADVERTISEMENT_QUERY_KEY } from "@pages/signage/components/AdvertisementSlider/hook"

type TimeoutType = NodeJS.Timeout | string | number | undefined
type IntervalType = NodeJS.Timeout | string | number | undefined
let SOCKET_SYNC_EVENTS: Record<string, boolean> = {}

export const useSignageIdleTimeout = (timeoutInMinutes = SIGNAGE_PAGE_IDLE_TIMEOUT_MINUTE) => {
  const timerRef = useRef<TimeoutType>(null)
  const intervalRef = useRef<IntervalType>(null)
  const isIdleReset = useRef<boolean>(false)
  const setInteractiveProductId = useSignageStore((state) => state.actions.setInteractiveProductId)
  const isProductDetailModalOpening = useSignageStore((state) => state.isProductDetailModalOpening)
  const removeAllTemporaryProducts = useSignageStore((state) => state.actions.removeAllTemporaryProducts)
  const setActiveTab = useSignageStore((state) => state.actions.setActiveTab)

  const startInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    intervalRef.current = setInterval(handleDelayedSocketEvents, timeoutInMinutes * 60 * 1000)
  }

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const resetTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    stopInterval()

    timerRef.current = setTimeout(handleClear, timeoutInMinutes * 60 * 1000)
  }

  useSyncData({
    onBeforeSync: (eventName: string, _: any) => {
      SOCKET_SYNC_EVENTS[eventName] = true
    },
  })

  const handleClear = () => {
    isIdleReset.current = true
    setInteractiveProductId(null)
    removeAllTemporaryProducts()
    handleDelayedSocketEvents().then(() => {
      setActiveTab(ROUTE.SIGNAGE_PRODUCTS_TOP)

      if (!isProductDetailModalOpening) {
        window.scrollTo(0, 0)
      }
    })
    startInterval()
  }

  useEffect(() => {
    if (!isProductDetailModalOpening && isIdleReset.current) {
      window.scrollTo(0, 0)
    }

    return () => {
      if (!isProductDetailModalOpening) {
        isIdleReset.current = false
      }
    }
  }, [isProductDetailModalOpening])

  useEffect(() => {
    const events = ["mousedown", "touchstart", "mousemove", "keydown", "scroll"]

    events.forEach((event) => {
      window.addEventListener(event, resetTimer)
    })
    resetTimer()

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)

      stopInterval()

      events.forEach((event) => {
        window.removeEventListener(event, resetTimer)
      })
    }
  }, [])

  return null
}

async function handleDelayedSocketEvents(): Promise<void> {
  if (!Object.keys(SOCKET_SYNC_EVENTS)) {
    return
  }

  const events = SOCKET_SYNC_EVENTS
  const { SIGNAGE_DATA_INVALIDATED, SIGNAGE_ADVERTISEMENT_INVALIDATED } = SOCKET_EVENTS

  if (events[SIGNAGE_DATA_INVALIDATED]) {
    await queryClient.invalidateQueries({ queryKey: ["signage"] })
  }

  if (events[SIGNAGE_ADVERTISEMENT_INVALIDATED]) {
    await queryClient.invalidateQueries({ queryKey: SIGNAGE_ADVERTISEMENT_QUERY_KEY, exact: true })
  }

  SOCKET_SYNC_EVENTS = {}
}
